import React from 'react'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { Dialog, DialogContent, DialogOverlay, Button, Input, Icon, IconButton } from '@shopflo/ui'
import { SealPercent, Sparkle, X } from '@phosphor-icons/react'
import { useDiscountForm } from '../hooks/useDiscountForm'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { createDiscountPayloadV2 } from 'src/lib/utils/discounts/discountsV2Helpers'
import { apiURI } from 'src/lib/utils/constants'
import { postRequest } from 'src/lib/core/api-client'
import InputField from 'src/components/common/Inputfield'
import BulletListEditor from 'src/components/common/form-elements/BulletListEditor'
import CouponCardPreview from './CouponCardPreview'

interface SaveDiscountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const staticOptions = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false
}

const SaveDiscountDialog = ({ open, onOpenChange }: SaveDiscountDialogProps) => {
  const router = useRouter()
  const { discountId } = router.query

  const {
    state,
    actions: { setFormState, handleSave }
  } = useDiscountForm()

  const {
    getters: { merchant }
  } = useMerchant()

  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const { configuration, entitlement } = state
  const { preview } = configuration
  const { applicationMethod } = entitlement

  const descriptionKey = open && merchant?.accountId
    ? [
        `/issuer/${issuerId}/merchant/${merchant?.accountId}/coupon/description`,
        entitlement.concessionAmount,
        entitlement.amountType,
        (entitlement as any).maximumConcessionAmount
      ]
    : null

  const { data: aiResponse, isValidating: isGeneratingAI } = useSWR(
    descriptionKey,
    async ([url]: [string]) => {
      const payload = createDiscountPayloadV2(state, merchant)
      const res: any = await postRequest(url, apiURI.DISCOUNT_ENGINE, payload)
      return res?.data?.data
    },
    {
      ...staticOptions,
      onSuccess: data => {
        if (data) {
          setFormState({
            configuration: {
              ...configuration,
              preview: {
                header: data?.header || '',
                description: data?.description || '',
                longDescription: data?.long_description || ''
              }
            }
          })
        }
      }
    }
  )

  const handleUpdatePreview = (updates: Partial<typeof preview>) => {
    setFormState({
      configuration: {
        ...configuration,
        preview: {
          ...preview,
          ...updates
        }
      }
    })
  }

  const handleDraftSave = () => {
    const newState = {
      ...state,
      prerequisite: {
        ...state.prerequisite,
        availability: {
          ...state.prerequisite.availability,
          active: false
        }
      }
    }
    setFormState({
      prerequisite: newState.prerequisite
    })
    handleSave(newState)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className='fixed inset-0 bg-black/40 backdrop-blur-sm z-40' />
      <DialogContent className='min-w-[900px] h-[540px] bg-white z-50 p-0 !rounded-3xl overflow-hidden border-none shadow-2xl'>
        <div className='flex h-[540px]'>
          {/* Left Column - Form */}
          <div className='w-[500px] flex flex-col gap-5 overflow-y-auto'>
            <div className='flex items-center gap-4 px-5 pt-4'>
              <div className='p-3 bg-zinc-50 rounded-2xl'>
                <Icon
                  IconComponent={SealPercent}
                  size='md'
                  className='text-zinc-500'
                  iconProps={{ weight: 'duotone' }}
                />
              </div>
              <h2 className='text-text-lg-semibold text-zinc-800'>Save Discount</h2>
            </div>

            <div className='flex flex-col gap-6 px-5'>
              <InputField
                label='Title'
                variant='outline'
                placeholder='Enter discount title'
                value={preview.header}
                maxSize={60}
                onChange={val => handleUpdatePreview({ header: val })}
              />
              <InputField
                label='Description'
                variant='outline'
                placeholder='Enter discount description'
                value={preview.description}
                maxSize={100}
                onChange={val => handleUpdatePreview({ description: val })}
              />

              {/* Details Section */}
              <div className='flex flex-col gap-1'>
                <div className='flex justify-between items-center'>
                  <label className='text-text-xs-medium  text-gray-dark'>Details</label>
                </div>
                <div className='p-4 bg-white rounded-xl border-1 border-zinc-200'>
                  <BulletListEditor
                    value={preview.longDescription}
                    onChange={val => handleUpdatePreview({ longDescription: val })}
                    placeholder='Add discount details...'
                    className='text-text-sm-regular text-zinc-700'
                  />
                </div>
              </div>
            </div>

            <div className='mt-auto flex items-center justify-between w-full px-5 pb-4'>
              <div>
                {!discountId && (
                  <Button variant='ghost' size='md' onClick={() => handleDraftSave()}>
                    Save as draft
                  </Button>
                )}
              </div>
              <div className='flex items-center gap-2'>
                <Button variant='outline' size='md' onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                {!discountId ? (
                  <Button variant='primary' size='md' onClick={() => handleSave()}>
                    Publish
                  </Button>
                ) : (
                  <Button variant='primary' size='md' onClick={() => handleSave()}>
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className='w-[400px] bg-zinc-100 flex items-center justify-center p-8 relative'>
            <Icon
              IconComponent={X}
              size='sm'
              className='absolute top-3 right-3 text-zinc-400 hover:text-zinc-600 cursor-pointer transition-colors'
              onClick={() => onOpenChange(false)}
            />

            <CouponCardPreview
              identifier={
                applicationMethod.method === 'CODE' ? applicationMethod.discountCode || '' : 'Applied Automatically'
              }
              descTitle={preview.header || 'Discount Title'}
              desc={preview.description || 'Applicable on eligible orders'}
              longDesc={preview.longDescription}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SaveDiscountDialog
